<template>
  <el-dialog
    :visible.sync="dialogVisible"
    title="印花变形(虚线内为控制点可操作范围,变形模式与移动模式在下方操作栏中)"
    width="fit-content"
    top="0px"
    @close="closeDialog"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
  >
    <div
      ref="editorContainer"
      class="editor-container"
      style="position: relative;"
    >
      <canvas
        ref="glCanvas"
        style="position: absolute; top: 0; left: 0; z-index: 1"
      ></canvas>
      <canvas
        ref="uiCanvas"
        style="
          position: absolute;
          z-index: 2;
          background-color: transparent;
          cursor: default;
          border: 1px dashed;
        "
      ></canvas>
    </div>

    <div
      style="
        display: flex;
        justify-content: center;
        gap: 10px;
        padding-top: 10px;
      "
    >
      <el-radio-group v-model="isShearMode" style="margin-right: 10px">
        <el-radio-button :label="0">自由变形</el-radio-button>
        <el-radio-button :label="1">斜切变形</el-radio-button>
        <el-radio-button :label="2">透视变形</el-radio-button>
      </el-radio-group>
      <el-button
        style="margin-right: 10px"
        :type="isPanMode ? 'primary' : 'default'"
        @click="togglePanMode"
        >全移动模式</el-button
      >
      <el-button type="primary" @click="handleConfirm">确认效果</el-button>
    </div>

    <div style="margin: 10px 0; text-align: center">
      <p v-if="isShearMode">
        <i class="el-icon-info"></i> 斜切/透视模式已启用 -
        只能拖动四个角落的控制点
      </p>
      <p v-else><i class="el-icon-info"></i> 自由模式 - 可以拖动所有控制点</p>
    </div>
  </el-dialog>
</template>
<script>
import PerspT from "perspective-transform";
export default {
  name: "Deformation",
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    image: {
      type: String,
      required: true,
    },
    backgroundImage: {
      type: String,
      required: true,
    },
    corners: {
      type: Array,
      required: true,
    },
    setCorners: {
      type: Array,
      required: true,
    },
    canvasWidth: {
      type: Number,
      default: 790,
    },
    canvasHeight: {
      type: Number,
      default: 790,
    },
    maskUrl: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      dialogVisible: this.visible,
      isShearMode: 0,
      localCorners: [...this.corners],
      gl: null,
      shaderProgram: null,
      surfaceTexture: null,
      controlPoints: [],
      meshPointss: [],
      defaultcontrolPoints: null,
      uiCtx: null,
      currentCtrlPoint: null,
      isDragging: false, // 新增：拖动状态
      pointRadius: 5, // 新增：控制点半径
      margin: 100,
      isPanMode: false, // 是否启用全移动模式
      isPanDragging: false, // 是否正在平移拖动
      panStart: { x: 0, y: 0 }, // 平移起始点
    };
  },
  watch: {
    visible(newVal) {
      this.dialogVisible = newVal;
      if (newVal) {
        this.$nextTick(() => {
          this.initEditor();
        });
      }
    },
    dialogVisible(val) {
      this.$emit("update:visible", val);
    },
    isShearMode() {
      this.renderScene();
    },
  },
  mounted() {
    if (this.visible) {
      this.$nextTick(() => {
        this.initEditor();
      });
    }
  },
  methods: {
    togglePanMode() {
      this.isPanMode = !this.isPanMode;
      this.isDragging = false;
      this.isPanDragging = false;
      this.currentCtrlPoint = null;
    },
    closeDialog() {
      this.$emit("close");
    },
    initEditor() {
      setTimeout(() => {
        // 初始化画布容器
        const container = this.$refs.editorContainer;
        // 容器大小要容纳扩展后的 uiCanvas
        container.style.width = `${this.canvasWidth}px`;
        container.style.height = `${this.canvasHeight}px`;
        container.style.margin = `${this.margin}px`;
        container.style.backgroundImage = `url(${this.backgroundImage})`;
        container.style.backgroundSize = "cover";

        // 初始化WebGL画布
        const glCanvas = this.$refs.glCanvas;
        glCanvas.width = this.canvasWidth;
        glCanvas.height = this.canvasHeight;

        if (this.maskUrl) {
          glCanvas.style.maskImage = `url(${this.maskUrl})`;
          glCanvas.style.maskSize = "100% 100%";
        }

        // 初始化UI画布
        const uiCanvas = this.$refs.uiCanvas;
        uiCanvas.width = this.canvasWidth + this.margin * 2;
        uiCanvas.height = this.canvasHeight + this.margin * 2;
        // 调整位置，使其相对 glCanvas 居中（左上角偏移）
        uiCanvas.style.left = `-${this.margin}px`;
        uiCanvas.style.top = `-${this.margin}px`;
        this.uiCtx = uiCanvas.getContext("2d");

        // 获取WebGL上下文
        this.gl = glCanvas.getContext("webgl2", {
          preserveDrawingBuffer: true,
          antialias: true,
          alpha: true,
        });
        if (!this.gl) {
          console.error("WebGL not supported");
          return;
        }

        // 初始化WebGL
        this.initWebGL();

        // 初始化控制点
        this.initControlPoints();

        // 加载图片纹理
        this.loadImageTexture();

        // 添加事件监听
        this.addEventListeners();
      }, 0);
    },

    // 添加事件监听
    addEventListeners() {
      const uiCanvas = this.$refs.uiCanvas;

      uiCanvas.addEventListener("pointerdown", (e) => {
        if (this.isPanMode) {
          // 全移动模式：记录起始点
          this.panStart = { x: e.clientX, y: e.clientY };
          this.isPanDragging = true;
        } else {
          const ctrlPointInfo = this.findCtrlPointInRange(e);
          if (ctrlPointInfo) {
            this.currentCtrlPoint = ctrlPointInfo;
            this.isDragging = true;
          }
        }
      });

      uiCanvas.addEventListener("pointermove", (e) => {
        if (this.isPanMode) {
          if (this.isPanDragging) {
            const dx = e.clientX - this.panStart.x;
            const dy = e.clientY - this.panStart.y;
            // 平移所有控制点
            for (let i = 0; i < 4; i++) {
              for (let j = 0; j < 4; j++) {
                this.controlPoints[i][j].x += dx;
                this.controlPoints[i][j].y += dy;
              }
            }
            this.panStart = { x: e.clientX, y: e.clientY };
            this.renderScene();
          }
          // 设置鼠标样式
          uiCanvas.style.cursor = this.isPanDragging ? "grabbing" : "grab";
        } else {
          if (this.isShearMode) {
            const ctrlPointInfo = this.findCtrlPointInRange(e);
            uiCanvas.style.cursor = ctrlPointInfo ? "move" : "default";
          } else {
            uiCanvas.style.cursor = this.findCtrlPointInRange(e)
              ? "move"
              : "default";
          }

          if (this.isDragging && this.currentCtrlPoint) {
            this.updateCurrentCtrlPointPos(e);
          }
        }
      });

      uiCanvas.addEventListener("pointerup", () => {
        this.isDragging = false;
        this.currentCtrlPoint = null;
        this.isPanDragging = false;
      });

      uiCanvas.addEventListener("pointerout", () => {
        this.isDragging = false;
        this.currentCtrlPoint = null;
        this.isPanDragging = false;
      });
    },

    // 查找控制点
    findCtrlPointInRange(e) {
      const rect = this.$refs.uiCanvas.getBoundingClientRect();
      const mousePos = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
      // 转换为 glCanvas 坐标系（减去 margin）
      const canvasX = mousePos.x - this.margin;
      const canvasY = mousePos.y - this.margin;

      let closestPoint = null;
      let minDist = this.pointRadius * 2;
      let isCorner = false;

      for (let i = 0; i < 4; i++) {
        for (let j = 0; j < 4; j++) {
          const point = this.controlPoints[i][j];
          // 直接使用 glCanvas 坐标与 canvasX, canvasY 比较
          const dx = point.x - canvasX;
          const dy = point.y - canvasY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist <= minDist) {
            if (this.isShearMode) {
              if ((i === 0 || i === 3) && (j === 0 || j === 3)) {
                minDist = dist;
                closestPoint = point;
                isCorner = true;
              }
            } else {
              minDist = dist;
              closestPoint = point;
              isCorner = (i === 0 || i === 3) && (j === 0 || j === 3);
            }
          }
        }
      }

      if (closestPoint) {
        return {
          point: closestPoint,
          originalPointX: closestPoint.x,
          originalPointY: closestPoint.y,
          originalMouseX: mousePos.x, // 原始鼠标坐标（大画布）
          originalMouseY: mousePos.y,
          isCorner: isCorner,
        };
      }
      return null;
    },

    // 更新控制点位置
    updateCurrentCtrlPointPos(e) {
      if (!this.currentCtrlPoint) return;
      const rect = this.$refs.uiCanvas.getBoundingClientRect();
      const mousePos = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
      // 计算鼠标在大画布上的总位移（像素）
      const deltaX = mousePos.x - this.currentCtrlPoint.originalMouseX;
      const deltaY = mousePos.y - this.currentCtrlPoint.originalMouseY;
      // 更新控制点的画布坐标（直接加上位移）
      this.currentCtrlPoint.point.x =
        this.currentCtrlPoint.originalPointX + deltaX;
      this.currentCtrlPoint.point.y =
        this.currentCtrlPoint.originalPointY + deltaY;

      // 可选：限制坐标范围（防止拖出太远）
      // this.currentCtrlPoint.point.x = Math.min(Math.max(this.currentCtrlPoint.point.x, -this.margin), this.canvasWidth + this.margin);
      // this.currentCtrlPoint.point.y = Math.min(Math.max(this.currentCtrlPoint.point.y, -this.margin), this.canvasHeight + this.margin);

      if (this.isShearMode && this.currentCtrlPoint.isCorner) {
        if (this.isShearMode == 1) this.updatePointsForShear();
        else this.updatePointsForShear2();
      }
      this.renderScene();
    },

    // 在斜切模式下更新控制点位置
    updatePointsForShear() {
      // 获取四个角落点
      const topLeft = this.controlPoints[0][0];
      const topRight = this.controlPoints[0][3];
      const bottomLeft = this.controlPoints[3][0];
      const bottomRight = this.controlPoints[3][3];

      // 更新顶部中间点
      for (let j = 1; j < 3; j++) {
        const t = j / 3;
        this.controlPoints[0][j].x = topLeft.x + (topRight.x - topLeft.x) * t;
        this.controlPoints[0][j].y = topLeft.y + (topRight.y - topLeft.y) * t;
      }

      // 更新底部中间点
      for (let j = 1; j < 3; j++) {
        const t = j / 3;
        this.controlPoints[3][j].x =
          bottomLeft.x + (bottomRight.x - bottomLeft.x) * t;
        this.controlPoints[3][j].y =
          bottomLeft.y + (bottomRight.y - bottomLeft.y) * t;
      }

      // 更新左侧中间点
      for (let i = 1; i < 3; i++) {
        const t = i / 3;
        this.controlPoints[i][0].x = topLeft.x + (bottomLeft.x - topLeft.x) * t;
        this.controlPoints[i][0].y = topLeft.y + (bottomLeft.y - topLeft.y) * t;
      }

      // 更新右侧中间点
      for (let i = 1; i < 3; i++) {
        const t = i / 3;
        this.controlPoints[i][3].x =
          topRight.x + (bottomRight.x - topRight.x) * t;
        this.controlPoints[i][3].y =
          topRight.y + (bottomRight.y - topRight.y) * t;
      }

      // 更新内部点
      for (let i = 1; i < 3; i++) {
        for (let j = 1; j < 3; j++) {
          // 水平插值
          const topX = this.controlPoints[0][j].x;
          const topY = this.controlPoints[0][j].y;
          const bottomX = this.controlPoints[3][j].x;
          const bottomY = this.controlPoints[3][j].y;

          // 垂直插值
          const v = i / 3;
          this.controlPoints[i][j].x = topX + (bottomX - topX) * v;
          this.controlPoints[i][j].y = topY + (bottomY - topY) * v;
        }
      }
    },

    // 在斜切模式下更新控制点位置，使用透视变换
    updatePointsForShear2() {
      // 获取四个角落点（原始的四个角点）
      const topLeft = this.controlPoints[0][0];
      const topRight = this.controlPoints[0][3];
      const bottomLeft = this.controlPoints[3][0];
      const bottomRight = this.controlPoints[3][3];
      // 使用透视变换库初始化
      const srcCorners = [
        topLeft.x,
        topLeft.y, // 左上角
        topRight.x,
        topRight.y, // 右上角
        bottomRight.x,
        bottomRight.y, // 右下角
        bottomLeft.x,
        bottomLeft.y, // 左下角
      ];
      // 初始化透视变换
      const perspT = PerspT(this.defaultcontrolPoints, srcCorners);
      this.meshPointss.forEach((srcPtList, i) => {
        srcPtList.forEach((srcPt, j) => {
          let dstPt = perspT.transform(srcPt.x, srcPt.y);
          this.controlPoints[i][j].x = dstPt[0];
          this.controlPoints[i][j].y = dstPt[1];
        });
      });
    },

    initWebGL() {
      // 顶点着色器源码
      const vsSource = `
            attribute vec2 aPosition;
            attribute vec2 aTexCoord;
            varying vec2 vTexCoord;
            void main() {
              gl_Position = vec4(aPosition, 0.0, 1.0);
              vTexCoord = aTexCoord;
            }
          `;

      // 片段着色器源码
      const fsSource = `
            precision mediump float;
            varying vec2 vTexCoord;
            uniform sampler2D uTexture;
            void main() {
              gl_FragColor = texture2D(uTexture, vTexCoord);
            }
          `;

      // 创建着色器程序
      this.shaderProgram = this.createShaderProgram(
        this.gl,
        vsSource,
        fsSource
      );
    },

    createShaderProgram(gl, vsSource, fsSource) {
      // 创建和编译着色器
      const vertexShader = this.loadShader(gl, gl.VERTEX_SHADER, vsSource);
      const fragmentShader = this.loadShader(gl, gl.FRAGMENT_SHADER, fsSource);

      // 创建着色器程序
      const shaderProgram = gl.createProgram();
      gl.attachShader(shaderProgram, vertexShader);
      gl.attachShader(shaderProgram, fragmentShader);
      gl.linkProgram(shaderProgram);

      if (!gl.getProgramParameter(shaderProgram, gl.LINK_STATUS)) {
        console.error(
          "Unable to initialize the shader program: " +
            gl.getProgramInfoLog(shaderProgram)
        );
        return null;
      }

      return shaderProgram;
    },

    loadShader(gl, type, source) {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);

      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error(
          "An error occurred compiling the shaders: " +
            gl.getShaderInfoLog(shader)
        );
        gl.deleteShader(shader);
        return null;
      }

      return shader;
    },

    initControlPoints() {
      // 根据传入的四个角坐标初始化控制点
      this.controlPoints = [
        [
          { ...this.localCorners[0], z: 0 },
          { x: 0, y: 0, z: 0 },
          { x: 0, y: 0, z: 0 },
          { ...this.localCorners[1], z: 0 },
        ],
        [
          { x: 0, y: 0, z: 0 },
          { x: 0, y: 0, z: 0 },
          { x: 0, y: 0, z: 0 },
          { x: 0, y: 0, z: 0 },
        ],
        [
          { x: 0, y: 0, z: 0 },
          { x: 0, y: 0, z: 0 },
          { x: 0, y: 0, z: 0 },
          { x: 0, y: 0, z: 0 },
        ],
        [
          { ...this.localCorners[3], z: 0 },
          { x: 0, y: 0, z: 0 },
          { x: 0, y: 0, z: 0 },
          { ...this.localCorners[2], z: 0 },
        ],
      ];

      // 计算中间控制点
      this.calculateIntermediatePoints();
    },

    calculateIntermediatePoints() {
      const [topLeft, topRight, bottomRight, bottomLeft] = this.localCorners;

      // 计算顶部边中间点
      for (let j = 1; j < 3; j++) {
        const t = j / 3;
        this.controlPoints[0][j] = {
          x: topLeft.x + (topRight.x - topLeft.x) * t,
          y: topLeft.y + (topRight.y - topLeft.y) * t,
          z: 0,
        };
      }

      // 计算底部边中间点
      for (let j = 1; j < 3; j++) {
        const t = j / 3;
        this.controlPoints[3][j] = {
          x: bottomLeft.x + (bottomRight.x - bottomLeft.x) * t,
          y: bottomLeft.y + (bottomRight.y - bottomLeft.y) * t,
          z: 0,
        };
      }

      // 计算左边中间点
      for (let i = 1; i < 3; i++) {
        const t = i / 3;
        this.controlPoints[i][0] = {
          x: topLeft.x + (bottomLeft.x - topLeft.x) * t,
          y: topLeft.y + (bottomLeft.y - topLeft.y) * t,
          z: 0,
        };
      }

      // 计算右边中间点
      for (let i = 1; i < 3; i++) {
        const t = i / 3;
        this.controlPoints[i][3] = {
          x: topRight.x + (bottomRight.x - topRight.x) * t,
          y: topRight.y + (bottomRight.y - topRight.y) * t,
          z: 0,
        };
      }

      // 计算内部控制点
      for (let i = 1; i < 3; i++) {
        for (let j = 1; j < 3; j++) {
          const topX = this.controlPoints[0][j].x;
          const topY = this.controlPoints[0][j].y;
          const bottomX = this.controlPoints[3][j].x;
          const bottomY = this.controlPoints[3][j].y;

          const v = i / 3;
          this.controlPoints[i][j] = {
            x: topX + (bottomX - topX) * v,
            y: topY + (bottomY - topY) * v,
            z: 0,
          };
        }
      }

      this.meshPointss = JSON.parse(JSON.stringify(this.controlPoints));
      this.defaultcontrolPoints = [
        this.controlPoints[0][0].x,
        this.controlPoints[0][0].y,
        this.controlPoints[0][3].x,
        this.controlPoints[0][3].y,
        this.controlPoints[3][3].x,
        this.controlPoints[3][3].y,
        this.controlPoints[3][0].x,
        this.controlPoints[3][0].y,
      ];
      if (this.setCorners.length > 0) {
        this.controlPoints = JSON.parse(JSON.stringify(this.setCorners));
      }
    },

    loadImageTexture() {
      const image = new Image();
      image.crossOrigin = "Anonymous";

      image.onload = () => {
        this.surfaceTexture = this.createTexture(this.gl, image);
        this.renderScene();
      };
      image.src = this.image;
    },

    createTexture(gl, image) {
      const texture = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, texture);

      // 设置纹理参数
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

      // 将图像上传到纹理
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        image
      );

      return texture;
    },

    createSurfaceMesh() {
      const positions = [];
      const texCoords = [];
      const indices = [];

      const uStep = 0.05;
      const vStep = 0.05;

      const surfacePoints = [];

      // 计算曲面上的点
      for (let u = 0; u <= 1.0000001; u += uStep) {
        const row = [];
        for (let v = 0; v <= 1.0000001; v += vStep) {
          const point = this.bicubic(u, v);
          row.push({
            point: point,
            uv: { u, v },
          });
        }
        surfacePoints.push(row);
      }

      // 创建顶点数据
      for (let i = 0; i < surfacePoints.length; i++) {
        for (let j = 0; j < surfacePoints[i].length; j++) {
          const { point, uv } = surfacePoints[i][j];
          const webglCoords = this.toWebGLCoords(point.x, point.y);
          positions.push(webglCoords.x, webglCoords.y);
          texCoords.push(uv.u, uv.v);
        }
      }

      // 创建索引
      for (let i = 0; i < surfacePoints.length - 1; i++) {
        for (let j = 0; j < surfacePoints[i].length - 1; j++) {
          const idx1 = i * surfacePoints[i].length + j;
          const idx2 = i * surfacePoints[i].length + j + 1;
          const idx3 = (i + 1) * surfacePoints[i].length + j;
          const idx4 = (i + 1) * surfacePoints[i].length + j + 1;

          indices.push(idx1, idx2, idx3);
          indices.push(idx2, idx4, idx3);
        }
      }

      return {
        positions: new Float32Array(positions),
        texCoords: new Float32Array(texCoords),
        indices: new Uint16Array(indices),
        indexCount: indices.length,
      };
    },
    bicubic(u, v) {
      const curvePoints = [];

      for (let i = 0; i < 4; i++) {
        curvePoints.push(
          this.cubic(
            this.controlPoints[i][0],
            this.controlPoints[i][1],
            this.controlPoints[i][2],
            this.controlPoints[i][3],
            u
          )
        );
      }

      return this.cubic(
        curvePoints[0],
        curvePoints[1],
        curvePoints[2],
        curvePoints[3],
        v
      );
    },
    cubic(p0, p1, p2, p3, t) {
      const t2 = t * t;
      const t3 = t2 * t;
      const mt = 1 - t;
      const mt2 = mt * mt;
      const mt3 = mt2 * mt;

      return {
        x: mt3 * p0.x + 3 * mt2 * t * p1.x + 3 * mt * t2 * p2.x + t3 * p3.x,
        y: mt3 * p0.y + 3 * mt2 * t * p1.y + 3 * mt * t2 * p2.y + t3 * p3.y,
      };
    },

    initBuffers(gl, mesh) {
      // 创建位置缓冲区
      const positionBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, mesh.positions, gl.STATIC_DRAW);

      // 创建纹理坐标缓冲区
      const texCoordBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, mesh.texCoords, gl.STATIC_DRAW);

      // 创建索引缓冲区
      const indexBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, mesh.indices, gl.STATIC_DRAW);

      return {
        position: positionBuffer,
        texCoord: texCoordBuffer,
        indices: indexBuffer,
        indexCount: mesh.indexCount,
      };
    },
    // 将画布坐标转换为WebGL坐标
    toWebGLCoords(x, y) {
      return {
        x: (x / this.canvasWidth) * 2 - 1,
        y: 1 - (y / this.canvasHeight) * 2,
      };
    },
    renderWebGLScene(buffers) {
      const gl = this.gl;
      gl.clearColor(0.0, 0.0, 0.0, 0.0);
      gl.clear(gl.COLOR_BUFFER_BIT);

      // 启用混合以实现透明度
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

      gl.useProgram(this.shaderProgram);

      // 绑定位置缓冲区
      const positionAttrib = gl.getAttribLocation(
        this.shaderProgram,
        "aPosition"
      );
      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.position);
      gl.vertexAttribPointer(positionAttrib, 2, gl.FLOAT, false, 0, 0);
      gl.enableVertexAttribArray(positionAttrib);

      // 绑定纹理坐标缓冲区
      const texCoordAttrib = gl.getAttribLocation(
        this.shaderProgram,
        "aTexCoord"
      );
      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.texCoord);
      gl.vertexAttribPointer(texCoordAttrib, 2, gl.FLOAT, false, 0, 0);
      gl.enableVertexAttribArray(texCoordAttrib);

      // 绑定纹理
      if (this.surfaceTexture) {
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.surfaceTexture);
        const textureLocation = gl.getUniformLocation(
          this.shaderProgram,
          "uTexture"
        );
        gl.uniform1i(textureLocation, 0);
      }

      // 绘制
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buffers.indices);
      gl.drawElements(gl.TRIANGLES, buffers.indexCount, gl.UNSIGNED_SHORT, 0);
    },

    // 渲染整个场景
    renderScene() {
      if (!this.surfaceTexture) return;

      const mesh = this.createSurfaceMesh();
      const buffers = this.initBuffers(this.gl, mesh);
      this.renderWebGLScene(buffers);
      this.renderControlGrid(); // 渲染控制点网格
    },
    renderControlGrid() {
      this.uiCtx.clearRect(
        0,
        0,
        this.$refs.uiCanvas.width,
        this.$refs.uiCanvas.height
      );
      for (let i = 0; i < 4; i++) {
        for (let j = 0; j < 4; j++) {
          const point = this.controlPoints[i][j];
          let pointColor;
          const uiX = point.x + this.margin; // 偏移后画布上的位置
          const uiY = point.y + this.margin;
          // 在斜切模式下，角落点为蓝色，其他点为灰色
          if (this.isShearMode) {
            if ((i === 0 || i === 3) && (j === 0 || j === 3)) {
              pointColor = "rgba(90, 168, 224, 1)"; // 可拖动的角落点
            } else {
              pointColor = "rgba(150, 150, 150, 1)"; // 不可拖动的点
            }
          } else {
            pointColor = "rgba(90, 168, 224, 1)"; // 所有点都可拖动
          }
          this.renderPoint(uiX, uiY, this.pointRadius, pointColor);
          // 绘制连线时同样需要偏移
          if (j < 3) {
            const nextPoint = this.controlPoints[i][j + 1];
            const nextUiX = nextPoint.x + this.margin;
            const nextUiY = nextPoint.y + this.margin;
            this.renderLine({ x: uiX, y: uiY }, { x: nextUiX, y: nextUiY });
          }
          if (i < 3) {
            const nextPoint = this.controlPoints[i + 1][j];
            const nextUiX = nextPoint.x + this.margin;
            const nextUiY = nextPoint.y + this.margin;
            this.renderLine({ x: uiX, y: uiY }, { x: nextUiX, y: nextUiY });
          }
        }
      }
    },

    renderPoint(x, y, r, color = "white") {
      this.uiCtx.fillStyle = color;
      this.uiCtx.beginPath();
      this.uiCtx.arc(x, y, r, 0, 2 * Math.PI);
      this.uiCtx.fill();
      this.uiCtx.closePath();
    },

    // 绘制线条
    renderLine(p1, p2, color = "rgba(90, 168, 224, 0.4)") {
      this.uiCtx.strokeStyle = color;
      this.uiCtx.lineWidth = 1;
      this.uiCtx.beginPath();
      this.uiCtx.moveTo(p1.x, p1.y);
      this.uiCtx.lineTo(p2.x, p2.y);
      this.uiCtx.setLineDash([5, 5]);
      this.uiCtx.stroke();
      this.uiCtx.closePath();
    },
    handleConfirm() {
      const corners = [
        { ...this.controlPoints[0][0] },
        { ...this.controlPoints[0][3] },
        { ...this.controlPoints[3][3] },
        { ...this.controlPoints[3][0] },
      ];
      if (!this.maskUrl) {
        const glCanvasUrl = this.$refs.glCanvas.toDataURL("image/png");
        this.$emit("confirm", this.controlPoints, glCanvasUrl);
      } else {
        this.applyMaskToCanvas(this.$refs.glCanvas, this.maskUrl)
          .then((glCanvasUrl) => {
            this.$emit("confirm", this.controlPoints, glCanvasUrl);
          })
          .catch((err) => {
            console.error("蒙版合成失败", err);
            const glCanvasUrl = this.$refs.glCanvas.toDataURL("image/png");
            this.$emit("confirm", this.controlPoints, glCanvasUrl);
          });
      }
    },
    applyMaskToCanvas(canvas, maskUrl) {
      return new Promise((resolve, reject) => {
        const maskImg = new Image();
        maskImg.crossOrigin = "Anonymous"; // 处理跨域
        maskImg.onload = () => {
          try {
            const offCanvas = document.createElement("canvas");
            offCanvas.width = canvas.width;
            offCanvas.height = canvas.height;
            const ctx = offCanvas.getContext("2d");

            // 1. 绘制蒙版（拉伸至全画布）
            ctx.drawImage(maskImg, 0, 0, offCanvas.width, offCanvas.height);
            // 2. 设置合成模式
            ctx.globalCompositeOperation = "source-in";
            // 3. 绘制原 canvas 内容
            ctx.drawImage(canvas, 0, 0);
            // 4. 导出
            const dataURL = offCanvas.toDataURL("image/png");
            resolve(dataURL);
          } catch (err) {
            reject(err);
          }
        };
        maskImg.onerror = () => reject(new Error("蒙版图片加载失败"));
        maskImg.src = maskUrl;
      });
    },
  },
};
</script>
<style scoped lang="scss">
::v-deep .el-dialog {
  border-radius: 15px;
  overflow: hidden;
  backdrop-filter: blur(10px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.5) !important;
}

::v-deep .el-dialog__header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 20px;
}

::v-deep .el-dialog__title {
  color: #feb47b !important;
  font-size: 1.4rem;
  font-weight: 600;
}

::v-deep .el-dialog__body {
  padding: 0 20px 0px !important;
}

::v-deep .el-dialog__headerbtn {
  top: 22px;
}

::v-deep .el-dialog__headerbtn .el-dialog__close {
  color: #ff7e5f !important;
  font-size: 1.4rem;
}

::v-deep .dialog-content {
  padding: 0 15px;
}
</style>